import { useQuery } from "@tanstack/react-query";
import { Megaphone, Pin } from "lucide-react";
import { useState } from "react";
import { useTenant } from "@/components/AppShell";
import { Badge, Card, cx } from "@/components/ui";
import { api, type Announcement } from "@/lib/api";
import { fmtRelative } from "@/lib/format";

import { t } from "@/lib/i18n";
/** Übersicht: News und Ankündigungen von Logiful (gepflegt unter „Logiful intern → News & Termine“) */
export function NewsCard({ showEmpty }: { showEmpty?: boolean } = {}) {
  const { tenantId } = useTenant();
  const q = useQuery({ queryKey: ["announcements", tenantId], queryFn: () => api.announcements(tenantId), staleTime: 5 * 60_000 });
  const [all, setAll] = useState(false);
  const items = q.data ?? [];
  if (!items.length)
    return showEmpty && q.isSuccess ? (
      <Card className="p-5">
        <h2 className="font-semibold flex items-center gap-2"><Megaphone className="size-4 text-fg-muted" /> {t("Neuigkeiten von Logiful")}</h2>
        <p className="mt-2 text-[13px] text-fg-muted">{t("Gerade keine Ankündigungen.")}</p>
      </Card>
    ) : null;
  const shown = all ? items : items.slice(0, 3);
  return (
    <Card className="p-5">
      <h2 className="font-semibold flex items-center gap-2 mb-3"><Megaphone className="size-4 text-fg-muted" /> {t("Neuigkeiten von Logiful")}</h2>
      <ul className="space-y-3">
        {shown.map((n) => <NewsItem key={n.id} n={n} />)}
      </ul>
      {items.length > 3 && (
        <button onClick={() => setAll(!all)} className="mt-3 text-[12.5px] text-fg-muted hover:text-fg">{all ? t("Weniger anzeigen") : t("Alle {length} anzeigen", { length: items.length })}</button>
      )}
    </Card>
  );
}

function NewsItem({ n }: { n: Announcement }) {
  const [open, setOpen] = useState(false);
  const long = n.body.length > 160 || n.body.includes("\n");
  const isNew = Date.now() - Date.parse(n.publishedAt) < 7 * 86_400_000;
  return (
    <li className={cx("rounded-xl border px-3.5 py-3", n.level === "important" ? "border-warning/40 bg-warning-soft/40" : "border-border")}>
      <div className="flex items-start gap-2">
        <p className="font-medium text-[13.5px] leading-snug flex-1 min-w-0">{t(n.title)}</p>
        {n.pinned && <Pin className="size-3.5 text-fg-subtle shrink-0 mt-0.5" aria-label={t("angeheftet")} />}
        {n.level === "important" ? <Badge tone="warning">{t("Wichtig")}</Badge> : isNew ? <Badge tone="info">{t("Neu")}</Badge> : null}
      </div>
      {n.body && (
        <p className={cx("mt-1 text-[12.5px] text-fg-muted whitespace-pre-line", !open && long && "line-clamp-2")}>
          <Linked text={n.body} />
        </p>
      )}
      <div className="mt-1.5 flex items-center gap-3 text-[11.5px] text-fg-subtle">
        <span>{fmtRelative(n.publishedAt)}</span>
        {long && <button onClick={() => setOpen(!open)} className="hover:text-fg">{open ? t("weniger") : t("weiterlesen")}</button>}
      </div>
    </li>
  );
}

/** Links im Text klickbar machen (nur http/https) – sonst reiner Text, kein HTML */
function Linked({ text }: { text: string }) {
  const parts = text.split(/(https?:\/\/[^\s)]+)/g);
  return <>{parts.map((p, i) => (/^https?:\/\//.test(p) ? <a key={i} href={p} target="_blank" rel="noreferrer noopener" className="underline underline-offset-2 hover:text-fg">{p}</a> : <span key={i}>{p}</span>))}</>;
}
